// src/hooks/useCustomFileMap.ts
import { useCallback, useRef, useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { FileNode, CustomFileMap, FileMapResponse, DeleteResponse, DeleteSummary, MoveResponse, RenameItemResponse } from '../types/FileManagerTypes';
import { ApiRoutes } from '../routes/routeConstants/apiRoutes';
import { useLocation } from 'react-router-dom';
import Notification from '../shared/components/Notification';
import { NotificationTypes } from '../enums/notificationTypes';
import { MutationKeys } from '../enums/mutationKeys';
import { QueryKeys } from '../enums/queryKeys';
import axiosInstance from '../interceptor/axiosInstance';
import { getApiErrorMessage } from '../shared/utils/apiErrorMessage';
import { buildApiPath } from '../shared/utils/buildApiPath';
import { withPreservedFolderChain } from '../views/FileManager/utils/filterFileMap';
import { useLegendForm } from '../context/MapLegendContext';

const MOVE_SUCCESS_FALLBACK = 'Selected Items and sub items moved successfully.';
const MOVE_ERROR_FALLBACK = 'Unable to move item(s). Please try again.';
const DELETE_ERROR_FALLBACK = 'Unable to delete item(s). Please try again.';
const RENAME_ERROR_FALLBACK = 'Unable to rename item. Please try again.';
const NOTIFICATION_DURATION_S = 4.5;

type MoveDestination = Pick<FileNode, 'id'>;

/** Outcome of a rename attempt; `message` carries the server's reason on failure. */
export type RenameItemResult = { ok: true } | { ok: false; message: string };

export interface FilterFilesParams {
  searchInput?: string;
  creator?: string[];
  fileType?: string[];
  map?: string[];
  fromDate?: string | null;
  toDate?: string | null;
  inRecent?: boolean;
  /** Folder id the filter is scoped to; the BE limits results to this location. */
  currentLocation?: string;
}

export const useCustomFileMap = (
  customFileMap: CustomFileMap,
  // The file browser re-fetches every section switch itself (filtered or not)
  // through useFileSearchAndFilters; the route effect below would race it.
  ownsRouteFetch = false,
) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { datasetId } = useLegendForm();

  const [fileMap, setFileMap] = useState(customFileMap);
  const [folderMap, setFolderMap] = useState(customFileMap);
  const [fileMapByFolderName, setFileMapByFolderName] = useState(customFileMap);
  // Empty until a listing response resolves it; never a placeholder id.
  const [rootFolderId, setRootFolderId] = useState<string>('');
  // Empty means "at root" — the effective ids are derived below, so a root
  // resolved after mount needs no resync.
  const [navigatedFolderId, setNavigatedFolderId] = useState<string>('');
  const [navigatedUploadFolderId, setNavigatedUploadFolderId] = useState<string>('');

  const currentFolderId = navigatedFolderId || rootFolderId;
  const uploadFolderId = navigatedUploadFolderId || rootFolderId;

  // Selecting the resolved root is stored as "at root" so it survives a root change.
  const setCurrentFolderId = useCallback((folderId: string) => setNavigatedFolderId(folderId === rootFolderIdRef.current ? '' : folderId), []);
  const setUploadFolderId = useCallback((folderId: string) => setNavigatedUploadFolderId(folderId === rootFolderIdRef.current ? '' : folderId), []);
  // Drives the file-list spinner during the (re)fetch of a section's mapping —
  // notably when switching between all-files / recents / trash.
  const [isMappingLoading, setIsMappingLoading] = useState<boolean>(true);

  const currentFolderIdRef = useRef(currentFolderId);
  const rootFolderIdRef = useRef(rootFolderId);
  // Only the most recently issued listing request may write the map: a search
  // cleared while its response is still in flight must not clobber the listing
  // fetched for the folder being restored.
  const listingRequestIdRef = useRef(0);

  useEffect(() => {
    updateMapping();
  }, []);
  useEffect(() => {
    currentFolderIdRef.current = currentFolderId;
  }, [currentFolderId]);
  useEffect(() => {
    rootFolderIdRef.current = rootFolderId;
  }, [rootFolderId]);
  useEffect(() => {
    if (!ownsRouteFetch) updateMapping();
  }, [location.pathname]);

  const updateMapping = useCallback(async () => {
    const isTrash = location.pathname === '/files/trash' ? 'true' : 'false';
    const isHomeView = location.pathname !== '/files/trash' && location.pathname !== '/files/recents';

    const requestId = ++listingRequestIdRef.current;
    setIsMappingLoading(true);
    try {
      let fileMapResponse;
      if (location.pathname === '/files/recents') {
        fileMapResponse = await axiosInstance.get<FileMapResponse>('/filemanager/recent-files');
      } else {
        fileMapResponse = await axiosInstance.get<FileMapResponse>('/filemanager/getfilemapping', {
          params: { isTrash },
        });
      }
      if (requestId !== listingRequestIdRef.current) return;
      setFileMap(fileMapResponse.data.fileMap);
      setRootFolderId(fileMapResponse.data.rootFolderId);

      // TODO: resolve list of calls with folder, filemapping details.
      // foldermapping retained trash folders and diverged from home. Destination
      // pickers (Upload / Move / Add Folder) are home-only and now use fileMap
      // from getfilemapping (isDir filtered at the consumer). Soft-removed the
      // dedicated /foldermapping fetch — mirror home fileMap only on home view
      // so destinations never point at trash/recents scopes.
      if (isHomeView) {
        setFolderMap(fileMapResponse.data.fileMap);
      }
    } catch (error) {
      console.error('Error fetching file mapping', error);
    } finally {
      setIsMappingLoading(false);
    }
  }, [location.pathname]);

  const filterFiles = useCallback(async (filterParams: FilterFilesParams) => {
    const { currentLocation, inRecent = false, ...rest } = filterParams;
    const requestId = ++listingRequestIdRef.current;
    try {
      const filterResponse = await axiosInstance.get<FileMapResponse>('/filemanager/filtered-files', {
        params: { ...rest, inRecent, currentLocation },
      });
      if (requestId !== listingRequestIdRef.current) return;
      setFileMap((prev) => withPreservedFolderChain(prev, filterResponse.data.fileMap, rootFolderIdRef.current, currentLocation));
      setRootFolderId(filterResponse.data.rootFolderId);
    } catch (error) {
      console.error('Error fetching file mapping', error);
      throw error;
    }
  }, []);

  const getFileLocation = async (fileId: string): Promise<any> => {
    try {
      const response = await axiosInstance.get<any>('/filemanager/filelocation', {
        params: { fileId },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching file location', error);
    }
  };

  const updateLastAccessTime = useCallback(async () => {
    // Derives from the root, so it is empty only before the first listing lands.
    const currentDirectoryId = currentFolderIdRef.current || rootFolderIdRef.current;
    if (!currentDirectoryId) return;
    try {
      const response = await axiosInstance.post('/filemanager/update-access-time', { currentDirectoryId });
      if (response.data.success) console.log('Updated Folder Access Time Successfully');
    } catch (error) {
      console.error('Error fetching file mapping', error);
    }
  }, []);

  const getFileMappingByFolderName = useCallback(async (folderName: string) => {
    try {
      const response = await axiosInstance.get<FileMapResponse>(`/filemanager/getfilemapping/${folderName}`);
      setFileMapByFolderName(response.data.fileMap);
      return response.data.fileMap;
    } catch (error) {
      console.error('Error fetching file mapping', error);
    }
  }, []);

  const { mutateAsync: deleteFilesAsync, isPending: isDeleting } = useMutation({
    mutationFn: (files: FileNode[]) =>
      axiosInstance.post(ApiRoutes.FILEMANAGER_DELETE_ITEMS, { idList: files.map((f) => f.id) }).then((res) => res.data),
  });

  // Blocks a second delete request from firing while one is still in flight
  // (US-108604 Scenario 1: "Duplicate delete requests shall be prevented").
  const isDeletingRef = useRef(false);

  const deleteFiles = useCallback(
    async (files: FileNode[]) => {
      if (isDeletingRef.current) return;
      isDeletingRef.current = true;
      try {
        const data = await deleteFilesAsync(files);
        if (data.success) {
          await updateMapping();
          queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_LAYER_GROUPS] });
          queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_MAP_LAYERS] });
          const count = files.length;
          Notification({
            message: count === 1 ? 'Item deleted successfully.' : `${count} items deleted successfully.`,
            type: NotificationTypes.SUCCESS,
          });
        }
      } catch (error) {
        console.error('Error permanently deleting files: ', error);
        Notification({ message: 'Unable to delete item(s). Please try again.', type: NotificationTypes.ERROR });
      } finally {
        isDeletingRef.current = false;
      }
    },
    [deleteFilesAsync, updateMapping],
  );

  // Soft-delete (move to Trash) — DELETE /filemanager/delete. The body carries the
  // user-selected top-level ids only; the server expands folder contents itself.
  // axios sends a DELETE body under the `data` config key, not as the 2nd argument.
  const { mutateAsync: trashFilesAsync } = useMutation({
    mutationFn: (itemIds: string[]) =>
      axiosInstance.delete<DeleteResponse>(ApiRoutes.FILEMANAGER_DELETE, { data: { itemIds } }).then((res) => res.data),
    onSuccess: async (data) => {
      await updateMapping();
      Notification({
        message: data.message,
        type: NotificationTypes.SUCCESS,
      });
    },
  });

  // Read-only delete preview: server-authoritative counts + map-usage warning.
  const { mutateAsync: fetchDeleteSummaryAsync } = useMutation({
    mutationFn: (itemIds: string[]) => axiosInstance.post<DeleteSummary>(ApiRoutes.FILEMANAGER_DELETE_SUMMARY, { itemIds }).then((res) => res.data),
  });

  const [isTrashing, setIsTrashing] = useState(false);
  // Blocks a second summary/trash request while one is in flight.
  const isTrashingRef = useRef(false);

  const getDeleteSummary = useCallback(
    async (itemIds: string[]): Promise<DeleteSummary | null> => {
      if (!itemIds.length || isTrashingRef.current) return null;
      isTrashingRef.current = true;
      setIsTrashing(true);
      try {
        return await fetchDeleteSummaryAsync(itemIds);
      } catch (error) {
        console.error('Error fetching delete summary: ', error);
        Notification({
          message: getApiErrorMessage(error, DELETE_ERROR_FALLBACK),
          type: NotificationTypes.ERROR,
        });
        return null;
      } finally {
        isTrashingRef.current = false;
        setIsTrashing(false);
      }
    },
    [fetchDeleteSummaryAsync],
  );

  const trashFiles = useCallback(
    async (itemIds: string[]) => {
      if (!itemIds.length || isTrashingRef.current) return;
      isTrashingRef.current = true;
      setIsTrashing(true);
      try {
        await trashFilesAsync(itemIds);
      } catch (error) {
        console.error('Error moving items to trash: ', error);
        Notification({
          message: getApiErrorMessage(error, DELETE_ERROR_FALLBACK),
          type: NotificationTypes.ERROR,
        });
      } finally {
        isTrashingRef.current = false;
        setIsTrashing(false);
      }
    },
    [trashFilesAsync],
  );

  const { mutateAsync: restoreFilesAsync } = useMutation({
    mutationFn: ({ files, moveToRoot }: { files: FileNode[]; moveToRoot: boolean }) =>
      axiosInstance.post(ApiRoutes.FILEMANAGER_RESTORE_ITEMS, { idList: files.map((f) => f.id), moveToRoot }).then((res) => res.data),
  });

  // Blocks a second restore request from firing while one is still in flight
  // (US-108675 Scenario 1: "Duplicate restore requests shall be prevented"),
  // matching the isDeletingRef pattern above.
  const isRestoringRef = useRef(false);

  const restoreFiles = useCallback(
    async (files: FileNode[], moveToRoot: boolean) => {
      if (isRestoringRef.current) return;
      isRestoringRef.current = true;
      try {
        const data = await restoreFilesAsync({ files, moveToRoot });
        const count = files.length;
        if (data.success) {
          await updateMapping();
          Notification({
            message: data.message ?? (count === 1 ? 'Item restored successfully.' : `${count} items restored successfully.`),
            type: NotificationTypes.SUCCESS,
          });
        } else {
          Notification({
            message: data.message ?? 'Unable to restore item(s). Please try again.',
            type: NotificationTypes.ERROR,
          });
        }
      } catch (error) {
        console.error('Error restoring files: ', error);
        const axiosError = error as AxiosError<{ message?: string }>;
        Notification({
          message: axiosError.response?.data?.message ?? 'Unable to restore item(s). Please try again.',
          type: NotificationTypes.ERROR,
        });
      } finally {
        isRestoringRef.current = false;
      }
    },
    [restoreFilesAsync, updateMapping],
  );

  // Blocks a second move request from firing while one is still in flight.
  const isMovingRef = useRef(false);
  const [isMoving, setIsMoving] = useState(false);

  // The server auto-renames on destination name collision and reports it per
  // item, so a silently renamed file isn't a surprise after the move.
  const resolveMoveSuccessMessage = (data: MoveResponse) => {
    const base = data.message || MOVE_SUCCESS_FALLBACK;
    const renamedCount = data.items?.filter((item) => item.renamed).length ?? 0;
    if (renamedCount === 0) return base;
    return renamedCount === 1
      ? `${base} 1 item was renamed to avoid a name conflict.`
      : `${base} ${renamedCount} items were renamed to avoid name conflicts.`;
  };

  const { mutateAsync: moveFilesAsync } = useMutation({
    mutationKey: [MutationKeys.MOVE_FILEMANAGER_ITEMS],
    mutationFn: ({ files, destinationFolderId }: { files: FileNode[]; destinationFolderId: string }) =>
      axiosInstance
        .post<MoveResponse>(ApiRoutes.FILEMANAGER_MOVE, {
          itemIds: files.map((f) => f.id),
          destinationFolderId,
        })
        .then((res) => res.data),
    onSuccess: async (data) => {
      await updateMapping();
      Notification({
        message: resolveMoveSuccessMessage(data),
        type: NotificationTypes.SUCCESS,
        duration: NOTIFICATION_DURATION_S,
      });
    },
    onError: (error) => {
      console.error('Error moving files: ', error);
      Notification({
        message: getApiErrorMessage(error, MOVE_ERROR_FALLBACK),
        type: NotificationTypes.ERROR,
        duration: NOTIFICATION_DURATION_S,
      });
    },
  });

  const moveFiles = useCallback(
    async (files: FileNode[], destination: MoveDestination, _source?: FileNode) => {
      if (!files.length || !destination?.id) return;
      if (isMovingRef.current) return;

      isMovingRef.current = true;
      setIsMoving(true);
      try {
        await moveFilesAsync({ files, destinationFolderId: destination.id });
      } catch {
        // Failure is surfaced by the mutation's onError handler above.
      } finally {
        isMovingRef.current = false;
        setIsMoving(false);
      }
    },
    [moveFilesAsync],
  );

  const createFolder = useCallback(
    async (folderName: string, currentDirectoryId?: string) => {
      try {
        const response = await axiosInstance.post('/filemanager/create-folder', {
          folderName,
          currentDirectoryId: currentDirectoryId || currentFolderIdRef.current || rootFolderIdRef.current,
        });

        if (response.data.success) {
          console.log('Folder Created successfully');
          setUploadFolderId(response.data.folder.folder_id);
          await updateMapping();
          return response.data.folder;
        }
      } catch (error) {
        console.error('Folder Creation Failed:', error);
        Notification({
          message: getApiErrorMessage(error, 'Unable to create folder. Please try again.'),
          type: NotificationTypes.ERROR,
        });
      }
    },
    [updateMapping],
  );

  const renameFile = useCallback(
    async (fileName: string, currentFileId: string): Promise<RenameItemResult> => {
      // Capture original name once so Strict Mode's double updater invoke still
      // rolls back to the pre-rename value on failure.
      const previousNameRef: { current?: string } = {};

      setFileMap((prev) => {
        const file = prev[currentFileId];
        if (!file || file.name === fileName) return prev;
        previousNameRef.current = file.name;
        return {
          ...prev,
          [currentFileId]: { ...file, name: fileName },
        };
      });

      try {
        // buildApiPath, not generatePath: ids may contain "/" (e.g.
        // "root/quote-evidence.pdf"), which must stay a single path segment.
        const response = await axiosInstance.patch<RenameItemResponse>(buildApiPath(ApiRoutes.FILEMANAGER_RENAME_ITEM, { itemId: currentFileId }), {
          newName: fileName,
        });

        // The server owns the final name -- it normalizes the input and re-appends
        // the original extension -- so echo its value rather than the typed one.
        const finalName = response.data.name;
        setFileMap((prev) => {
          const file = prev[currentFileId];
          if (!file) return prev;
          return {
            ...prev,
            [currentFileId]: { ...file, name: finalName },
          };
        });

        // Refetch so grouped members (renamed shapefile parts) stay in sync.
        await updateMapping();
        return { ok: true };
      } catch (error) {
        console.error('File rename failed:', error);

        if (previousNameRef.current !== undefined) {
          const previousName = previousNameRef.current;
          setFileMap((prev) => {
            const file = prev[currentFileId];
            if (!file) return prev;
            return {
              ...prev,
              [currentFileId]: { ...file, name: previousName },
            };
          });
        }

        return { ok: false, message: getApiErrorMessage(error, RENAME_ERROR_FALLBACK) };
      }
    },
    [updateMapping],
  );

  const editShapeFile = useCallback(
    (files: FileNode[], name: string, layerDetails?: any, callback?: () => void) => {
      if (files.length < 3) return;
      const idList = files.map((a) => a.id);
      axiosInstance
        .put('/filemanager/parse-shape-file', {
          idList,
          currentDirectoryId: currentFolderIdRef.current,
          name,
          ...layerDetails,
        })
        .then((response) => {
          if (response.data.success) {
            updateMapping();
            callback && callback();
            Notification({ message: 'Layer Updated Successfully', description: response?.data?.message, type: NotificationTypes.SUCCESS });
          }
        })
        .catch((error) => {
          Notification({ message: 'Failed to update layer', description: error?.response?.data?.error, type: NotificationTypes.ERROR });
        });
    },
    [updateMapping],
  );

  const createImageLayer = useCallback(
    (imageFiles: FileNode[]) => {
      if (imageFiles.length != 1) return;
      axiosInstance
        .post('/filemanager/create-image-layer', { imageId: imageFiles[0].id })
        .then((response) => {
          if (response.data.success) console.log('Created Image Layer Successfully');
        })
        .catch(console.error);
    },
    [updateMapping],
  );

  const removeImageLayer = useCallback(
    (imageFiles: FileNode[]) => {
      if (imageFiles.length != 1) return;
      axiosInstance
        .post('/filemanager/remove-image-layer', { imageId: imageFiles[0].id })
        .then((response) => {
          if (response.data.success) console.log('Removed Image Layer Successfully');
        })
        .catch(console.error);
    },
    [updateMapping],
  );

  // `silent` lets the caller suppress the built-in success/error toasts so
  // it can emit AC-specific copy itself (US-101662 Scenario 8 / 11 — the
  // symbology save path needs exact-text notifications). Errors still
  // propagate either way so the caller can branch on success vs failure.
  const saveLayerToGeoserver = async (
    layerName: string,
    mapId: string,
    layerDetails: any,
    uploadFileExtension: string,
    isReupload: boolean,
    callback: () => void,
    silent = false,
  ) => {
    try {
      // Strip client-only / selection / query fields so Joi SAVE_LAYER schema
      // never rejects with e.g. `"body.matchFilter" is not allowed`.
      const {
        isExistingLayer,
        createdAt,
        updatedAt,
        toggled,
        setState,
        highlightColor,
        selectedFeatureIds,
        selectionCql,
        matchFilter,
        matchExtent,
        matchCql,
        proximityMatch,
        matchSink,
        matchOrder,
        matchInvert,
        excludedFeatureIds,
        includedFeatureIds,
        excludedIds,
        includedIds,
        selectAllMatching,
        reverseActive,
        displaySelectionOnly,
        keyField,
        ...requiredLayerDetails
      } = layerDetails || {};
      const response = await axiosInstance.post(ApiRoutes.SAVE_LAYER, {
        layerName,
        mapId,
        uploadFileExtension,
        isReupload,
        ...requiredLayerDetails,
        // Both new layers and reuploads must identify the logical dataset so the
        // backend can resolve the correct revision/table rather than inferring it
        // from a possibly stale folder selection.
        ...(datasetId ? { datasetId } : {}),
      });
      if (response.data) {
        callback && callback();
        if (!silent) {
          Notification({ message: response?.data?.message, type: NotificationTypes.SUCCESS });
        }
      }
    } catch (error: any) {
      console.error('Error saving layer to Geoserver:', error);
      if (!silent) {
        Notification({ message: error?.message ?? 'Save to Geoserver failed', type: NotificationTypes.ERROR });
      }
      throw error;
    }
  };

  return {
    fileMap,
    folderMap,
    rootFolderId,
    currentFolderId,
    filterFiles,
    fileMapByFolderName,
    setCurrentFolderId,
    uploadFolderId,
    setUploadFolderId,
    getFileLocation,
    deleteFiles,
    isDeleting,
    trashFiles,
    getDeleteSummary,
    isTrashing,
    restoreFiles,
    moveFiles,
    isMoving,
    createFolder,
    renameFile,
    updateMapping,
    isMappingLoading,
    updateLastAccessTime,
    createImageLayer,
    removeImageLayer,
    getFileMappingByFolderName,
    editShapeFile,
    saveLayerToGeoserver,
  };
};
